import { useQuery } from "@apollo/client";
import { Combobox } from "@/components/Combobox";
import { type ComboboxOption } from "@/components/Combobox/types";
import { graphql, type ResultOf } from "@/graphql";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { removeNothings } from "@/lib/utils/arrays";
import { sortOptionsByLabel } from "@/lib/utils/sorting";

type Props = {
  id?: string;
  selected: Array<string>;
  onSelect: (values: Array<string>) => void;
  className?: string;
  placeholder: string;
  inputRef?: React.Ref<HTMLButtonElement>;
  isModal?: boolean;
};

export type LabelsData = ResultOf<typeof labelsQuery>;

export function LabelsPicker({ id, selected, onSelect, className, placeholder, inputRef, isModal }: Props) {
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { data, loading, error } = useQuery(labelsQuery, {
    variables: { businessUnitId: activeBusinessUnitId || "" },
  });

  const options: Array<ComboboxOption> = getLabelOptions(data);

  return (
    <Combobox
      id={id}
      name="labels"
      options={options}
      selected={selected}
      onChange={onSelect}
      loading={loading}
      error={error}
      type="multi"
      className={className}
      placeholder={placeholder}
      inputRef={inputRef}
      isModal={isModal}
    />
  );
}

export const getLabelOptions = (data: LabelsData | undefined) => {
  const options = sortOptionsByLabel(
    removeNothings(data?.supplyChainBookingLabels || []).map((label) => {
      return {
        label: label.label,
        value: label.id,
      };
    }),
  );

  return options;
};

export const labelsQuery = graphql(`
  query SupplyChainBookingLabels($businessUnitId: ID!) {
    supplyChainBookingLabels(businessUnit: $businessUnitId) {
      status
      label
      id
    }
  }
`);
